'use client';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import WindowSize from '@/utils/windowSize';
import Container from '@/components/layout/Container';
import { CompareSettings } from './CompareSettings';
import DesktopStickyFollow from './DesktopStickyFollow';
import MobileStickyFollow from './MobileStickyFollow';
import CompareLayers from './CompareLayers';
import CompareRTE from './CompareRTE';
import CompareTags from './CompareTags';
import ImageCards from './ImageCards';
import CompareAttributes from './CompareAttributes';

/**
 * The main section featured on the compare mattresses page.
 */
export default function CompareMattresses({ block }) {
  const entry = block?.compare_mattresses?.reference?.[0];
  if (!entry?.products?.length || !entry?.journeys?.length) return null;

  return <CompareTable entry={entry} />;
}

function CompareTable({ entry }) {
  const { products, journeys, uid: entryUid } = entry;
  const windowSize = WindowSize();

  const [desktopSelectors, setDesktopSelectors] = useState([]);
  const [mobileSelectors, setMobileSelectors] = useState([]);
  const [state, setState] = useState({
    mobileSelectors: mobileSelectors,
    desktopSelectors: desktopSelectors
  });

  const [journeyTitle, setJourneyTitle] = useState('');
  const [journeyDescription, setJourneyDescription] = useState('');

  useEffect(() => {
    setState({
      mobileSelectors: mobileSelectors,
      desktopSelectors: desktopSelectors
    });
  }, [mobileSelectors, desktopSelectors]);

  //get array of journey queries
  const journeysArr = useMemo(
    () => journeys.map((j) => j.query.toLowerCase()),
    [journeys]
  );

  const hasInitialized = useRef(false);

  useEffect(() => {
    function getMobileSelectors(index) {
      return Object.values(journeys[index].mobile_dropdowns)?.map((column) => {
        return {
          productTitle: column,
          index: products.indexOf(
            products.find(
              (item) =>
                item.reference[0].shopifyProduct[0].title ===
                column.concat(' Mattress')
            )
          )
        };
      });
    }

    function getDesktopSelectors(index) {
      return Object.values(journeys[index].desktop_dropdowns)?.map((column) => {
        return {
          productTitle: column,
          index: products.indexOf(
            products.find(
              (item) =>
                item.reference[0].shopifyProduct[0].title ===
                column.concat(' Mattress')
            )
          )
        };
      });
    }
    // Would like to use useSearchParams but it requires this to be wrapped
    // with a suspense boundary
    const params = new URLSearchParams(window.location.search);
    if (hasInitialized.current) return;

    hasInitialized.current = true;

    const cqTerm = params.get('cq_term');
    const cmTerm = params.get('cm_term');

    //check to see if router path matches any journey queries
    if (journeysArr.includes(cqTerm || cmTerm)) {
      const queryIndex = journeys.findIndex(
        (journey) => journey.query.toLowerCase() === (cqTerm || cmTerm)
      );

      if (journeys[queryIndex].heading_title) {
        setJourneyTitle(journeys[queryIndex].heading_title);
      } else {
        setJourneyTitle('Compare Mattresses');
      }

      if (journeys[queryIndex].heading_description) {
        setJourneyDescription(journeys[queryIndex].heading_description);
      } else {
        setJourneyDescription('Select mattresses to compare below.');
      }
      setDesktopSelectors(getDesktopSelectors(queryIndex));
      setMobileSelectors(getMobileSelectors(queryIndex));
    } else {
      //if no matching journey, set heading and selectors to first journey in array (stock/default)
      if (!journeys[0].heading_title == '') {
        setJourneyTitle(journeys[0].heading_title);
      } else {
        setJourneyTitle('Compare Mattresses');
      }

      if (journeys[0].heading_description) {
        setJourneyDescription(journeys[0].heading_description);
      } else {
        setJourneyDescription('Select mattresses to compare below');
      }
      setDesktopSelectors(getDesktopSelectors(0));
      setMobileSelectors(getMobileSelectors(0));
    }
  }, [journeysArr, journeys, products]);

  const [onDesktop, setOnDesktop] = useState(
    windowSize.width < 1024 ? false : true
  );

  //format products so they have an index to match to and a disabled status
  const formattedProducts = products.map((product, index) => {
    return {
      productTitle: product.reference[0].shopifyProduct[0].title.replace(
        ' Mattress',
        ''
      ),
      index: index,
      disabled: false
    };
  });

  const [filteredProducts, setFilteredProducts] = useState(formattedProducts);

  useEffect(() => {
    const updatedProducts = formattedProducts.map((product) => {
      if (onDesktop) {
        if (desktopSelectors?.some((item) => item.index === product.index)) {
          return { ...product, disabled: true };
        }
      } else {
        if (mobileSelectors?.some((item) => item.index === product.index)) {
          return { ...product, disabled: true };
        }
      }
      return { ...product, disabled: false };
    });

    // Only update state if the new data is different
    if (JSON.stringify(updatedProducts) !== JSON.stringify(filteredProducts)) {
      setFilteredProducts(updatedProducts);
    }
  }, [
    desktopSelectors,
    mobileSelectors,
    onDesktop,
    formattedProducts,
    filteredProducts
  ]);

  useEffect(() => {
    if (windowSize.width >= 1024) {
      setOnDesktop(true);
    } else {
      setOnDesktop(false);
    }
  }, [windowSize.width]);

  const [showHeaderSticky, setHeaderSticky] = useState(false);

  function scrollListener() {
    const currentSelectorPosition =
      document.querySelector('.main-shop-button')?.getBoundingClientRect().top +
      window.pageYOffset;
    if (window.scrollY >= currentSelectorPosition) {
      setHeaderSticky(true);
    } else {
      setHeaderSticky(false);
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', scrollListener);

    return () => window.removeEventListener('scroll', scrollListener);
  }, []);

  //when desktop selector is changed
  function DesktopSelect({ product, index }) {
    const [selectedItem, setSelectedItem] = useState();

    function onSelectedItemChange(value) {
      setSelectedItem(
        filteredProducts.find((item) => item.productTitle == value)
      );
      const changes = {
        selectedItem: filteredProducts.find(
          (item) => item.productTitle == value
        )
      };

      const tempArr = [...desktopSelectors];

      //get text of selected items from each selector
      const list = document.querySelectorAll(`.compare-select`);
      const newSelections = Array.from(list).map((elem) => {
        return elem.value;
      });

      //get previously selected items titles to compare to selected
      const oldSelections = desktopSelectors.map((item) => {
        return item.productTitle;
      });
      //compare old and new selections and get index of item that doesn't match
      let removeIndex;
      oldSelections.map((item, index) => {
        if (newSelections[index] !== item) {
          removeIndex = index;
        }
      });

      //replace old selection with new selection in desktopSelectors array
      tempArr.splice(removeIndex, 1, {
        productTitle: changes.selectedItem.productTitle,
        index: parseInt(changes.selectedItem.index),
        disabled: true
      });
      setDesktopSelectors(tempArr);
    }

    return (
      <div
        className={`desktop-selectors-${index} compare-selector-wrapper relative`}
      >
        <div className="flex">
          <label
            htmlFor={`compare-select-${index}`}
            className="compare-selector sr-only"
            aria-label="Select a mattress"
          >
            Select product to compare in column {index + 1}
          </label>
          <select
            defaultValue={selectedItem ? selectedItem.productTitle : product}
            className={`compare-select focus-visible:ring-blue block w-full cursor-pointer bg-white px-6 py-3 focus-visible:border focus-visible:border-black focus-visible:ring-2`}
            name={`compare-select-${index}`}
            id={`compare-select-${index}`}
            onChange={(e) => onSelectedItemChange(e.target.value)}
            data-cy={`compare-select-${index}`}
          >
            {filteredProducts.map((item, optionIndex) => (
              <option
                id={`${item.productTitle.replaceAll(' ', '-')}-${optionIndex}-${index}`}
                className={`compare-item-${index} w-full px-6 py-4 sm:px-7 lg:px-8 ${item.disabled ? 'text-dawn-grey-60' : 'text-black'} flex flex-col`}
                key={`${item.productTitle.replaceAll(' ', '-')}-${optionIndex}-${index}`}
                value={item.productTitle}
                disabled={item.disabled ? true : false}
              >
                {item.productTitle}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  //when mobile selector is changed
  function MobileSelect({ product, index }) {
    const [selectedItem, setSelectedItem] = useState();

    function onSelectedItemChange(value) {
      setSelectedItem(
        filteredProducts.find((item) => item.productTitle == value)
      );
      const changes = {
        selectedItem: filteredProducts.find(
          (item) => item.productTitle == value
        )
      };

      const tempArr = [...mobileSelectors];

      //get text of selected items from each selector
      const list = document.querySelectorAll(`.compare-select`);
      const newSelections = Array.from(list).map((elem) => {
        return elem.value;
      });

      //get previously selected items titles to compare to selected
      const oldSelections = mobileSelectors.map((item) => {
        return item.productTitle;
      });
      //compare old and new selections and get index of item that doesn't match
      let removeIndex;
      oldSelections.map((item, index) => {
        if (newSelections[index] !== item) {
          removeIndex = index;
        }
      });
      //replace old selection with new selection in mobileSelectors array
      tempArr.splice(removeIndex, 1, {
        productTitle: changes.selectedItem.productTitle,
        index: parseInt(changes.selectedItem.index),
        disabled: true
      });
      setMobileSelectors(tempArr);
    }

    return (
      <div className="desktop-selectors compare-selector-wrapper relative">
        <div className="flex">
          <label
            htmlFor={`compare-select-${index}`}
            aria-label="Select a mattress"
            className="sr-only"
          >
            Select product to compare in column {index + 1}
          </label>
          <select
            defaultValue={selectedItem ? selectedItem.productTitle : product}
            className={`compare-select mt-2 block w-full cursor-pointer truncate border-0 bg-white py-[14px] pr-10 pl-5 ring-1 ring-gray-300 ring-inset focus:ring-2 focus:ring-black sm:pr-5`}
            name={`compare-select-${index}`}
            id={`compare-select-${index}`}
            onChange={(e) => onSelectedItemChange(e.target.value)}
          >
            {filteredProducts.map((item, optionIndex) => {
              return (
                <option
                  id={`${item.productTitle.replaceAll(' ', '-')}-${optionIndex}-${index}`}
                  className={`compare-item-${index} w-full px-6 py-4 sm:px-7 lg:px-8 ${item.disabled ? 'text-dawn-grey-60' : 'text-black'} flex flex-col`}
                  key={`${item.productTitle.replaceAll(' ', '-')}-${optionIndex}-${index}`}
                  value={item.productTitle}
                  disabled={item.disabled ? true : false}
                >
                  {item.productTitle}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div id="compare-mattresses" className="standard-padding">
      {onDesktop ? (
        <DesktopStickyFollow
          showHeaderSticky={showHeaderSticky}
          desktopSelectors={desktopSelectors}
          filteredProducts={filteredProducts}
          DesktopSelect={DesktopSelect}
          products={products}
        />
      ) : (
        <MobileStickyFollow
          showHeaderSticky={showHeaderSticky}
          mobileSelectors={mobileSelectors}
          filteredProducts={filteredProducts}
          MobileSelect={MobileSelect}
          products={products}
        />
      )}
      <Container>
        <div>
          <h1 className="h3 lg:h1 min-h-[54px] pb-[10px] text-center lg:min-h-[76px] lg:pb-[24px]">
            {journeyTitle}
          </h1>
          <h2 className="body-r lg:body-l min-h-[100px] pb-[50px] text-center lg:min-h-[130px] lg:pb-[82px]">
            {journeyDescription}
          </h2>
        </div>
        <div
          className={`main-compare-selectors grid min-h-15 lg:min-h-[50px] ${onDesktop ? 'grid-cols-3 gap-x-[32px]' : 'grid-cols-2 gap-x-[14px] sm:gap-x-[24px]'}`}
        >
          {onDesktop
            ? desktopSelectors?.map((product, index) => {
                return (
                  <DesktopSelect
                    product={product.productTitle}
                    index={index}
                    key={`selector-${index}`}
                  />
                );
              })
            : mobileSelectors?.map((product, index) => {
                return (
                  <MobileSelect
                    product={product.productTitle}
                    index={index}
                    key={`selector-${index}`}
                  />
                );
              })}
        </div>
      </Container>
      <CompareSettings.Provider value={state}>
        {(entry.modular_blocks ?? []).map((block, index) => {
          switch (Object.keys(block)[0]) {
            case 'compare_layers':
              return (
                <CompareLayers
                  key={index}
                  block={block}
                  position={index}
                  entryUid={entryUid}
                  onDesktop={onDesktop}
                />
              );
            case 'compare_rte':
              return (
                <CompareRTE
                  key={index}
                  block={block}
                  position={index}
                  entryUid={entryUid}
                  onDesktop={onDesktop}
                />
              );
            case 'compare_tags':
              return (
                <CompareTags
                  key={index}
                  block={block}
                  position={index}
                  entryUid={entryUid}
                  onDesktop={onDesktop}
                />
              );
            case 'image_cards':
              return (
                <ImageCards
                  key={index}
                  block={block}
                  position={index}
                  products={products}
                  entryUid={entryUid}
                  onDesktop={onDesktop}
                />
              );
            case 'compare_attributes':
              return (
                <CompareAttributes
                  key={index}
                  block={block}
                  position={index}
                  entryUid={entryUid}
                  onDesktop={onDesktop}
                />
              );
            default:
              return (
                <div
                  key={`row-${index}${Object.keys(block)[0]}`}
                  className="empty-component"
                ></div>
              );
          }
        })}
      </CompareSettings.Provider>
    </div>
  );
}
